import type {
  ProductBbox,
  ProductListItem,
} from '@travel-planner/shared/schemas';
import type { ComponentProps } from 'react';
import type { PlannerBooking } from '../model/host';
import type {
  AdvisorDraftItem,
  AdvisorProduct,
  AdvisorProductType,
  AdvisorRegion,
} from '../model/types';
import type { PlannerServerSort } from '../model/v3-results';
import type { DaySuggestions } from '../model/v3-suggestions';
import type { AdvisorMap } from './advisor-map';
import type { FilterBar } from './filter-bar';
import type { ItineraryRail } from './itinerary-rail';
import type { ProductStack } from './product-stack';
// Leaf import, not the entity barrel: the barrel re-exports XT-only UI that
// the cms-public storefront (which mounts this workspace) cannot resolve.
import {
  useAdultTravellerTypeIds,
  useChildTravellerTypeIds,
} from '@products/entities/product/api/use-adult-traveller-type-ids';
import { useEffect, useMemo, useRef, useState } from 'react';
import { dayRegionRefusal, itemRegionRefusal } from '../model/add-item';
import {
  autoSelectForSection,
  deriveActiveRegionIds,
  labelsForLandingRegion,
  labelsForSelectedParents,
  moodsFromRegionLabels,
  regionsMatchingMoods,
  removeChapter,
  sameIdList,
  selectChapter,
  setActiveChapter,
  visibleSectionTags,
} from '../model/cms-mood';
import {
  regionMatchesCountry,
  regionsForCountry,
  resolveSelectedCountry,
} from '../model/country';
import {
  chapterGenerateCity,
  hotelPreferencesFromRequestNotes,
  keywordsForGenerate,
  regionHubCity,
  seedCitiesForCountry,
} from '../model/generate-input';
import {
  DETAILS_LOOKUP_LIMIT,
  pendingDetailIds,
  withProductDetails,
} from '../model/item-details';
import { mapListItemToAdvisorProduct } from '../model/map-product';
import {
  displayRegionTitle,
  matchRegionFromBrief,
  regionName,
} from '../model/region-layout';
import { useAdvisorPlannerStore } from '../model/store';
import { resolveAdvisorThemes } from '../model/themes';
import {
  ADVISOR_PAGE_SIZE,
  preferredPace,
  resolveAdvisorRegionId,
  resolveAdvisorRegionIds,
} from '../model/to-query-input';
import { advisorProductTypes } from '../model/types';
import { itineraryProductIds } from '../model/v3-results';
import { usePlannerHost } from './planner-host';
import { useAdvisorDnd } from './use-advisor-dnd';

/** The agent's pick of region for a product the planner could not place. */
const inRegion = (
  product: AdvisorProduct,
  region: AdvisorRegion | undefined
): AdvisorProduct =>
  region
    ? { ...product, regionId: region.id, regionTitle: region.title }
    : product;

export type AdvisorFiltersPatch = {
  country?: string | null;
  region?: string[] | null;
  active?: string | null;
  mood?: string[] | null;
  type?: AdvisorProductType;
  page?: number | null;
};

export type AdvisorPlannerInput = {
  country: string;
  regionIds: string[];
  activeRegionId: string | null;
  selectedMoods: string[];
  type: AdvisorProductType;
  page: number;
  seedCities?: readonly string[];
  seedDurationDays?: number;
  seedMonth?: number;
  seedNotes?: string | null;
  seedDestination?: string | null;
  /** The brief's pace and whether children travel — order-only Suggestions signals. */
  briefPace?: 'relaxed' | 'balanced' | 'active' | null;
  childrenInParty?: boolean;
  /** Free text over the catalogue (v3 search box). */
  search?: string;
  /** Map viewport when "Only what is on the map" is on. */
  bbox?: ProductBbox | null;
  /** The results' catalogue order (price, most booked). */
  sort?: PlannerServerSort | null;
  /** Hotels only: star classes and price levels to keep (empty: all). */
  hotelStars?: readonly number[];
  hotelPriceLevels?: readonly number[];
  /** Per-type and off-map counts for the v3 filters; off for the console. */
  withCounts?: boolean;
  /** The brief's dates and party, for the dynamic products' prices. */
  booking?: PlannerBooking | null;
  onFiltersChange: (next: AdvisorFiltersPatch) => void;
  /** Why a drop was refused (another region's day), for the host to show. */
  onRefuse?: (message: string) => void;
};

export type AdvisorPlannerCounts = {
  /** Results per type under the current filters (tags only on the chosen type). */
  byType: Record<AdvisorProductType, number | null>;
  /** Results in view (the map area when it filters). */
  inView: number;
  /** Results matching the filters, wherever they are. */
  matching: number | null;
};

/**
 * Filters, catalogue, map, rail and drag state for one planner. Layouts
 * (console workspace, Mini Planner v3) only arrange the returned props.
 */
export const useAdvisorPlanner = ({
  country,
  regionIds,
  activeRegionId,
  selectedMoods,
  type,
  page,
  seedCities = [],
  seedDurationDays,
  seedMonth,
  seedNotes = null,
  seedDestination = null,
  briefPace = null,
  childrenInParty = false,
  search,
  bbox = null,
  sort = null,
  hotelStars = [],
  hotelPriceLevels = [],
  withCounts = false,
  booking = null,
  onFiltersChange,
  onRefuse,
}: AdvisorPlannerInput) => {
  const host = usePlannerHost();
  const {
    regions: listedRegions,
    regionItems: listedRegionItems,
    countries,
    isLoading: regionsLoading,
  } = host.useRegions(country);
  const regions = useMemo(
    () => regionsForCountry(listedRegions, country),
    [country, listedRegions]
  );
  const regionItems = useMemo(
    () =>
      listedRegionItems.filter((row) =>
        row.countries.some(
          (entry) => entry.toLowerCase() === country.trim().toLowerCase()
        )
      ),
    [country, listedRegionItems]
  );
  const {
    moods: mapMoods,
    mapRegions = [],
    isLoading: moodsLoading,
  } = host.useMoods(country);
  const { tags: tagPool, isLoading: tagsLoading } = host.useTags(type, country);
  const [tagOverrideByKey, setTagOverrideByKey] = useState<
    Record<string, string[]>
  >({});
  const parentRegions = useMemo(
    () => regions.filter((region) => region.parentId === null),
    [regions]
  );
  const labelMoods = useMemo(
    () => moodsFromRegionLabels(parentRegions),
    [parentRegions]
  );
  const moods =
    moodsLoading && mapMoods.length === 0
      ? []
      : mapMoods.length > 0
        ? mapMoods
        : labelMoods;
  const knownMoods = moodsLoading
    ? selectedMoods
    : resolveAdvisorThemes(selectedMoods, moods);
  const moodedRegions = useMemo(
    () =>
      regions.map((region) => ({
        ...region,
        labels: labelsForLandingRegion(region, mapRegions),
      })),
    [mapRegions, regions]
  );
  const listedRegionIds = resolveAdvisorRegionIds(regionIds, regions);
  const activeKnown = resolveAdvisorRegionId(activeRegionId, regions);
  const mapFocusId =
    activeKnown && listedRegionIds.includes(activeKnown) ? activeKnown : null;
  const filterRegionIds = deriveActiveRegionIds(listedRegionIds, mapFocusId);
  const moodRegionIds = useMemo(
    () => regionsMatchingMoods(moodedRegions, knownMoods),
    [knownMoods, moodedRegions]
  );
  const activeTitle = mapFocusId
    ? (regions.find((region) => region.id === mapFocusId)?.title ?? null)
    : null;
  const focusedRegionMoods = useMemo(
    () =>
      mapFocusId ? labelsForSelectedParents(moodedRegions, [mapFocusId]) : [],
    [mapFocusId, moodedRegions]
  );
  const autoTags = useMemo(
    () => autoSelectForSection(tagPool, focusedRegionMoods),
    [focusedRegionMoods, tagPool]
  );
  const tagKey = `${mapFocusId ?? 'country'}:${type}`;
  const activeTags = tagOverrideByKey[tagKey] ?? autoTags;
  const shownTags = visibleSectionTags(tagPool, activeTags);

  const productQuery = {
    country,
    selectedRegionIds: filterRegionIds,
    tags: tagPool,
    regions,
    page,
    search,
  };
  const { items, isLoading, isError, total, products } = host.useProducts({
    ...productQuery,
    type,
    selectedTags: tagsLoading ? [] : activeTags,
    bbox,
    preferPace: preferredPace(type, briefPace),
    sort,
    hotelStars,
    hotelPriceLevels,
    booking,
  });
  // Fixed hook order: one count query per other type, one without the map.
  // Wait for the visible page before badge counts so XT is not 4-way contended
  // on first paint (portal batches the two type counts into one server fn).
  const countsEnabled = withCounts && !isLoading;
  const [otherA, otherB] = advisorProductTypes.filter(
    (entry) => entry !== type
  ) as [AdvisorProductType, AdvisorProductType];
  const countA = host.useProducts({
    ...productQuery,
    type: otherA,
    selectedTags: [],
    bbox,
    countOnly: true,
    enabled: countsEnabled,
  });
  const countB = host.useProducts({
    ...productQuery,
    type: otherB,
    selectedTags: [],
    bbox,
    countOnly: true,
    enabled: countsEnabled,
  });
  const unbounded = host.useProducts({
    ...productQuery,
    type,
    selectedTags: tagsLoading ? [] : activeTags,
    bbox: null,
    hotelStars,
    hotelPriceLevels,
    countOnly: true,
    enabled: countsEnabled && Boolean(bbox),
  });
  const hotelPreferences = useMemo(
    () => hotelPreferencesFromRequestNotes(seedNotes),
    [seedNotes]
  );
  const generateDraft = host.useGenerate({
    country,
    keywords: keywordsForGenerate(knownMoods, seedNotes),
    emptyDurationDays: seedDurationDays,
    seedCities,
    seedMonth,
    hotelPreferences,
    pace: briefPace,
  });
  const totalPages = Math.max(1, Math.ceil(total / ADVISOR_PAGE_SIZE));
  useEffect(() => {
    if (!isLoading && total > 0 && page > totalPages) {
      onFiltersChange({ page: totalPages });
    }
  }, [isLoading, onFiltersChange, page, total, totalPages]);
  useEffect(() => {
    if (regionsLoading || (selectedMoods.length > 0 && moodsLoading)) return;
    const nextCountry = resolveSelectedCountry(country, countries);
    const knownRegions = [...listedRegionIds];
    if (activeKnown && !knownRegions.includes(activeKnown)) {
      knownRegions.push(activeKnown);
    }
    const nextActive =
      activeKnown && knownRegions.includes(activeKnown) ? activeKnown : null;
    const regionOk = sameIdList(knownRegions, regionIds);
    const activeOk = nextActive === activeRegionId;
    const moodOk =
      knownMoods.length === selectedMoods.length &&
      knownMoods.every((entry, index) => entry === selectedMoods[index]);
    if (nextCountry === country && regionOk && activeOk && moodOk) return;
    onFiltersChange({
      country: nextCountry === country ? undefined : nextCountry,
      region: regionOk
        ? undefined
        : knownRegions.length > 0
          ? knownRegions
          : null,
      active: activeOk ? undefined : nextActive,
      mood: moodOk ? undefined : knownMoods.length > 0 ? knownMoods : null,
    });
  }, [
    activeKnown,
    activeRegionId,
    countries,
    country,
    knownMoods,
    listedRegionIds,
    mapFocusId,
    onFiltersChange,
    regionIds,
    regions,
    regionsLoading,
    selectedMoods,
    moodsLoading,
  ]);
  const seededRegionRef = useRef<string | null>(null);
  const countryChosenRef = useRef(false);
  useEffect(() => {
    if (regionsLoading || countryChosenRef.current) return;
    const match = matchRegionFromBrief(seedDestination, regions);
    if (!match || !regionMatchesCountry(match, country)) return;
    if (regionIds.length > 0 || activeRegionId) {
      seededRegionRef.current = match.id;
      return;
    }
    if (seededRegionRef.current === match.id) return;
    seededRegionRef.current = match.id;
    onFiltersChange({ region: [match.id], active: match.id, page: null });
  }, [
    activeRegionId,
    country,
    onFiltersChange,
    regionIds,
    regions,
    regionsLoading,
    seedDestination,
  ]);
  const draft = useAdvisorPlannerStore((s) => s.draft);
  const addProduct = useAdvisorPlannerStore((s) => s.addProduct);
  const addProductToDay = useAdvisorPlannerStore((s) => s.addProductToDay);
  const addProductToNewDay = useAdvisorPlannerStore(
    (s) => s.addProductToNewDay
  );
  const addDay = useAdvisorPlannerStore((s) => s.addDay);
  const removeLeg = useAdvisorPlannerStore((s) => s.removeLeg);
  const removeDay = useAdvisorPlannerStore((s) => s.removeDay);
  const removeDayService = useAdvisorPlannerStore((s) => s.removeDayService);
  const restoreDayService = useAdvisorPlannerStore((s) => s.restoreDayService);
  const restoreTransfer = useAdvisorPlannerStore((s) => s.restoreTransfer);
  const removeTransferLine = useAdvisorPlannerStore(
    (s) => s.removeTransferLine
  );
  const setTransferVehicles = useAdvisorPlannerStore(
    (s) => s.setTransferVehicles
  );
  const setTransferPlace = useAdvisorPlannerStore((s) => s.setTransferPlace);
  const setTransferTime = useAdvisorPlannerStore((s) => s.setTransferTime);
  const setTransferAssistance = useAdvisorPlannerStore(
    (s) => s.setTransferAssistance
  );
  const setDayTransportVehicles = useAdvisorPlannerStore(
    (s) => s.setDayTransportVehicles
  );
  const setDayTransportSpan = useAdvisorPlannerStore(
    (s) => s.setDayTransportSpan
  );
  const setItemComment = useAdvisorPlannerStore((s) => s.setItemComment);
  const setHotelBooking = useAdvisorPlannerStore((s) => s.setHotelBooking);
  const setItemTime = useAdvisorPlannerStore((s) => s.setItemTime);
  const setProductGuests = useAdvisorPlannerStore((s) => s.setProductGuests);
  const removeItem = useAdvisorPlannerStore((s) => s.removeItem);
  const reorderItems = useAdvisorPlannerStore((s) => s.reorderItems);
  const moveItemToDay = useAdvisorPlannerStore((s) => s.moveItemToDay);
  const setChapterHotel = useAdvisorPlannerStore((s) => s.setBaseHotel);
  const removeBaseHotel = useAdvisorPlannerStore((s) => s.removeBaseHotel);
  const removeSuggested = useAdvisorPlannerStore((s) => s.removeSuggested);
  const waiting = useAdvisorPlannerStore((s) => s.suggestions);
  const acceptSuggestion = useAdvisorPlannerStore((s) => s.acceptSuggestion);
  const skipSuggestion = useAdvisorPlannerStore((s) => s.skipSuggestion);
  const dismissSuggestions = useAdvisorPlannerStore(
    (s) => s.dismissSuggestions
  );
  const suggestionIds = useMemo(
    () => [
      ...new Set(
        Object.values(waiting).flatMap((items) =>
          items.map((item) => item.productId)
        )
      ),
    ],
    [waiting]
  );
  const railPending = useMemo(() => pendingDetailIds(draft), [draft]);
  const detailIds = useMemo(
    () =>
      [...new Set([...suggestionIds, ...railPending])].slice(
        0,
        DETAILS_LOOKUP_LIMIT
      ),
    [railPending, suggestionIds]
  );
  const detailKey = detailIds.join(',');
  // Generate items and picks carry no price, map point or duration: look them
  // up with the same row the result cards use.
  const detailLookup = host.useProducts({
    type,
    country,
    selectedRegionIds: [],
    selectedTags: [],
    tags: [],
    regions,
    page: 1,
    ids: detailIds,
    enabled: detailIds.length > 0,
  });
  const lookupBusy = detailLookup.isLoading || Boolean(detailLookup.isFetching);
  const lookupSettled =
    detailIds.length > 0 && !lookupBusy && !detailLookup.isError;
  const fillDetails = useAdvisorPlannerStore((s) => s.fillItemDetails);
  // Every chapter is one of the country's regions: ones named after a city
  // (older drafts, Generate stays) move to the region that holds them.
  const assignRegions = useAdvisorPlannerStore((s) => s.assignRegions);
  useEffect(() => {
    if (regionsLoading || regions.length === 0 || draft.legs.length === 0) {
      return;
    }
    assignRegions(regions, country);
  }, [assignRegions, country, draft, regions, regionsLoading]);
  useEffect(() => {
    if (!lookupSettled) return;
    fillDetails(detailKey.split(','), detailLookup.products);
  }, [detailKey, detailLookup.products, fillDetails, lookupSettled]);
  const [detailProductId, setDetailProductId] = useState<string | null>(null);
  const adultTravellerIds = useAdultTravellerTypeIds();
  const childTravellerIds = useChildTravellerTypeIds();

  // The price the card showed: the host priced it with the organisation's
  // traveller types, which the portal's tRPC stub cannot load here.
  const toAdvisorProduct = (item: ProductListItem): AdvisorProduct => {
    const mapped = mapListItemToAdvisorProduct(
      item,
      regions,
      mapFocusId,
      adultTravellerIds,
      childTravellerIds
    );
    const listed = products.find((product) => product.id === item.id);
    return listed
      ? {
          ...mapped,
          clientPrice: listed.clientPrice,
          childPrice: listed.childPrice ?? null,
        }
      : mapped;
  };

  const handleAdd = (item: ProductListItem) => {
    addProduct(toAdvisorProduct(item));
  };
  // A drop on another region's day is refused with the reason, not silently.
  const refused = (message: string | null): boolean => {
    if (message) onRefuse?.(message);
    return Boolean(message);
  };
  const dayOfItem = (itemId: string) =>
    draft.legs
      .flatMap((leg) => leg.days)
      .find((day) => day.items.some((item) => item.id === itemId))?.id;
  const dnd = useAdvisorDnd({
    onDropOnDay: (product, dayId) => {
      if (refused(dayRegionRefusal(draft, product, dayId, regionName))) return;
      addProductToDay(product, dayId);
    },
    onDropOnRail: addProduct,
    onReorderItems: (activeItemId, overItemId) => {
      const dayId = dayOfItem(overItemId);
      if (
        dayId &&
        refused(itemRegionRefusal(draft, activeItemId, dayId, regionName))
      ) {
        return;
      }
      reorderItems(activeItemId, overItemId);
    },
    onMoveItemToDay: (itemId, dayId) => {
      if (refused(itemRegionRefusal(draft, itemId, dayId, regionName))) return;
      moveItemToDay(itemId, dayId);
    },
    products,
  });

  const applyChapter = (next: { region: string[]; active: string | null }) => {
    onFiltersChange({
      region: next.region.length > 0 ? next.region : null,
      active: next.active,
    });
  };

  const filterBar: ComponentProps<typeof FilterBar> = {
    activeRegionId: mapFocusId,
    country,
    countries,
    moodRegionIds,
    moods,
    onRemoveRegion: (id) =>
      applyChapter(removeChapter(listedRegionIds, mapFocusId, id)),
    onSelectCountry: (next) => {
      countryChosenRef.current = true;
      seededRegionRef.current = null;
      setTagOverrideByKey({});
      onFiltersChange({
        country: next,
        region: null,
        active: null,
        mood: null,
      });
    },
    onSelectMood: (name) => {
      const nextMoods = selectedMoods.includes(name)
        ? selectedMoods.filter((entry) => entry !== name)
        : [...selectedMoods, name];
      onFiltersChange({
        mood: nextMoods.length > 0 ? nextMoods : null,
      });
    },
    onSelectRegion: (id) => {
      applyChapter(
        listedRegionIds.includes(id)
          ? setActiveChapter(listedRegionIds, mapFocusId, id)
          : selectChapter(listedRegionIds, id)
      );
    },
    onSelectTag: (name) => {
      const current = activeTags;
      const next = current.includes(name)
        ? current.filter((entry) => entry !== name)
        : [...current, name];
      setTagOverrideByKey((prev) => ({ ...prev, [tagKey]: next }));
      onFiltersChange({ page: null });
    },
    onSelectType: (next) => onFiltersChange({ type: next }),
    regions: parentRegions,
    selectedMoods,
    selectedRegionIds: listedRegionIds,
    selectedTags: activeTags,
    selectedType: type,
    tags: shownTags,
  };

  const map: ComponentProps<typeof AdvisorMap> = {
    country,
    focusedRegionId: mapFocusId,
    isError,
    isLoading,
    items,
    moodRegionIds,
    onProductClick: setDetailProductId,
    onSelectRegion: (id) => {
      if (!id) {
        onFiltersChange({ active: null });
        return;
      }
      applyChapter(selectChapter(listedRegionIds, id));
    },
    regionItems: regionItems.filter((row) => row.parentId === null),
    selectedRegionIds: listedRegionIds,
  };

  const productStack: ComponentProps<typeof ProductStack> = {
    isError,
    isLoading,
    items,
    onAdd: handleAdd,
    onOpenDetails: setDetailProductId,
    onPageChange: (next) => onFiltersChange({ page: next }),
    page,
    regionTitles: activeTitle ? [displayRegionTitle(activeTitle)] : [],
    total,
    type,
  };

  // Trip Generate: EVERY selected region chip's hub city (not only map focus).
  // Active chapter still scopes the product stack; Generate plans the whole
  // shortlist so Madrid+Barcelona gets both overnights.
  const generateCitiesFor = (ids: readonly string[]): string[] =>
    ids.flatMap((id) => {
      const region = regions.find((entry) => entry.id === id);
      const hub = region ? regionHubCity(region) : null;
      return hub ? [hub] : [];
    });
  const dayCities = (legId: string): string[] | undefined => {
    const leg = draft.legs.find((entry) => entry.id === legId);
    const city = leg ? chapterGenerateCity(leg, regions) : null;
    return city ? [city] : undefined;
  };
  const daySuggestRegionIds = (legId: string): string[] => {
    const leg = draft.legs.find((entry) => entry.id === legId);
    if (leg?.regionId) return [leg.regionId];
    if (filterRegionIds.length > 0) return filterRegionIds;
    return [...listedRegionIds];
  };

  const rail: ComponentProps<typeof ItineraryRail> = {
    draft,
    generateError: generateDraft.isError,
    generatingDayId: generateDraft.generatingDayId,
    isGenerating: generateDraft.isGenerating,
    onAddDay: addDay,
    onGenerate: () =>
      generateDraft.generateAll(
        listedRegionIds.length > 0
          ? generateCitiesFor(listedRegionIds)
          : seedCitiesForCountry(country, seedDestination, seedCities)
      ),
    onGenerateDay: (legId: string, dayId: string) =>
      generateDraft.generateDay(legId, dayId, { cities: dayCities(legId) }),
    onRemoveItem: removeItem,
    onRemoveLeg: removeLeg,
    onRemoveSuggested: removeSuggested,
  };

  const inTrip = itineraryProductIds(draft);
  const lookedUp = new Map(
    detailLookup.products.map((product) => [product.id, product])
  );
  const suggestionsByDay: DaySuggestions = {};
  for (const [dayId, items] of Object.entries(waiting)) {
    const open = items.filter((item) => !inTrip.has(item.productId));
    // Keep an empty list so the day still shows "Nothing new to suggest"
    // instead of looking like Generate did nothing.
    suggestionsByDay[dayId] =
      open.length > 0
        ? open.map((item) =>
            withProductDetails(item, lookedUp.get(item.productId))
          )
        : [];
  }
  // The lookup keeps its previous rows while it fetches new ones: an item is
  // loading until its own row arrives.
  const itemDetails = {
    /** Product ids whose price, map point and duration are loading. */
    pendingIds: new Set(
      lookupBusy ? detailIds.filter((id) => !lookedUp.has(id)) : []
    ),
  };
  const suggestions = {
    byDay: suggestionsByDay,
    /** Generate for one day, offered as suggestions (Mini Planner v3). */
    onGenerate: (legId: string, dayId: string) =>
      generateDraft.generateDay(legId, dayId, {
        suggest: true,
        cities: dayCities(legId),
        // Leg's region first; else selected chips. Never country-wide if the
        // agent picked chapters — catalogue must stay inside those polygons.
        regionIds: daySuggestRegionIds(legId),
        // User type-tags + brief moods → sold-history rank keywords.
        categoryHashtags: [
          ...new Set([
            ...(tagsLoading ? [] : activeTags),
            ...keywordsForGenerate(knownMoods, seedNotes),
          ]),
        ],
        briefMoods: knownMoods,
        briefPace,
        childrenInParty,
      }),
    onAccept: (dayId: string, item: AdvisorDraftItem) =>
      acceptSuggestion(dayId, item),
    onSkip: (dayId: string, item: AdvisorDraftItem) =>
      skipSuggestion(dayId, item.id),
    onDismiss: dismissSuggestions,
  };

  const counts: AdvisorPlannerCounts = {
    byType: {
      [type]: isLoading ? null : total,
      [otherA]: countA.isLoading ? null : countA.total,
      [otherB]: countB.isLoading ? null : countB.total,
    } as Record<AdvisorProductType, number | null>,
    inView: total,
    matching: bbox ? (unbounded.isLoading ? null : unbounded.total) : total,
  };

  /** Regions, moods and this view's tags back to none; type and country stay. */
  const clearFilters = () => {
    // Clearing the region lands on the country-wide view: its tags go too.
    setTagOverrideByKey((prev) => ({
      ...prev,
      [tagKey]: [],
      [`country:${type}`]: [],
    }));
    onFiltersChange({ region: null, active: null, mood: null, page: null });
  };

  return {
    counts,
    clearFilters,
    /** The country's regions (with sub-regions): chapters are these. */
    countryRegions: regions,
    /** The page's products as the rail stores them (regions, prices, points). */
    products,
    addToDay: addProductToDay,
    /** A new day at the end of the product's (or the picked) region chapter. */
    addToNewDay: (product: AdvisorProduct, region?: AdvisorRegion) =>
      addProductToNewDay(inRegion(product, region)),
    /**
     * Makes the hotel its region chapter's base hotel (adding the chapter),
     * for `nights` nights — the chapter's day count.
     */
    setBaseHotel: (
      item: ProductListItem,
      region?: AdvisorRegion,
      nights?: number
    ) => setChapterHotel(inRegion(toAdvisorProduct(item), region), nights),
    removeBaseHotel,
    /** Drops a day with its items; a chapter keeps its last day. */
    removeDay,
    removeDayService,
    restoreDayService,
    restoreTransfer,
    removeTransferLine,
    setTransferVehicles,
    setTransferPlace,
    setTransferTime,
    setTransferAssistance,
    setDayTransportVehicles,
    setDayTransportSpan,
    setItemComment,
    setHotelBooking,
    setItemTime,
    setProductGuests,
    /** Every tag of the chosen type (the filter bar shows only a few). */
    allTags: tagPool,
    filterBar,
    map,
    productStack,
    rail,
    suggestions,
    itemDetails,
    dnd,
    detail: {
      Modal: host.DetailModal ?? null,
      productId: detailProductId,
      close: () => setDetailProductId(null),
    },
  };
};
